import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { FileText, Download, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface PaymentHistory {
  id: string;
  stripe_invoice_id: string | null;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  period_start: string | null;
  period_end: string | null;
  paid_at: string | null;
  refund_amount: number;
  refunded_at: string | null;
  metadata: any;
}

interface InvoiceListProps {
  businessId: string;
  businessName: string;
}

export default function InvoiceList({ businessId, businessName }: InvoiceListProps) {
  const [invoices, setInvoices] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
  }, [businessId]);

  async function loadInvoices() {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_billing_history', {
        p_business_id: businessId,
        p_limit: 50,
        p_offset: 0,
      });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error: any) {
      toast.error('שגיאה בטעינת החשבוניות');
    } finally {
      setLoading(false);
    }
  }

  function getStatusColor(status: string) {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-orange-600 bg-orange-50';
      case 'refunded':
      case 'partially_refunded':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'refunded':
      case 'partially_refunded':
        return <RefreshCw className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  }

  function getStatusText(status: string) {
    switch (status) {
      case 'paid':
        return 'שולם';
      case 'failed':
        return 'נכשל';
      case 'pending':
        return 'ממתין';
      case 'refunded':
        return 'הוחזר';
      case 'partially_refunded':
        return 'הוחזר חלקית';
      default:
        return status;
    }
  }

  function getPaymentTypeText(type: string) {
    switch (type) {
      case 'subscription':
        return 'מנוי';
      case 'one_time':
        return 'תשלום חד פעמי';
      case 'terminal':
        return 'טרמינל';
      case 'addon':
        return 'תוסף';
      default:
        return type;
    }
  }

  async function downloadInvoice(invoice: PaymentHistory) {
    try {
      setDownloadingId(invoice.id);

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;

      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('INVOICE', margin, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('LoyalFlow Business Management', margin, 28);

      doc.setTextColor(0, 0, 0);
      let y = 55;

      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y, pageWidth - 2 * margin, 35, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Bill To:', margin + 5, y + 8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(businessName, margin + 5, y + 15);
      doc.text(`Invoice ID: ${invoice.stripe_invoice_id || invoice.id.substring(0, 8)}`, margin + 5, y + 22);
      doc.text(`Date: ${invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : 'N/A'}`, margin + 5, y + 29);

      y += 45;

      const tableData = [];

      tableData.push([
        'Description',
        'Type',
        'Status',
        'Amount'
      ]);

      const description = invoice.period_start && invoice.period_end
        ? `Subscription billing for period ${new Date(invoice.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(invoice.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : 'Payment';

      tableData.push([
        description,
        getPaymentTypeText(invoice.payment_type),
        getStatusText(invoice.status),
        `${invoice.currency.toUpperCase()} ${invoice.amount.toFixed(2)}`
      ]);

      if (invoice.refund_amount > 0) {
        tableData.push([
          'Refund',
          'Refund',
          'Refunded',
          `-${invoice.currency.toUpperCase()} ${invoice.refund_amount.toFixed(2)}`
        ]);
      }

      (doc as any).autoTable({
        startY: y,
        head: [tableData[0]],
        body: tableData.slice(1),
        theme: 'grid',
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 5
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: margin, right: margin }
      });

      y = (doc as any).lastAutoTable.finalY + 10;

      doc.setFillColor(245, 245, 245);
      doc.rect(pageWidth - margin - 60, y, 60, 25, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Total Amount:', pageWidth - margin - 55, y + 8);

      doc.setFontSize(14);
      doc.setTextColor(37, 99, 235);
      const totalAmount = invoice.amount - invoice.refund_amount;
      doc.text(
        `${invoice.currency.toUpperCase()} ${totalAmount.toFixed(2)}`,
        pageWidth - margin - 5,
        y + 18,
        { align: 'right' }
      );

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      y = pageHeight - 30;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      y += 8;
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for your business!', margin, y);
      y += 5;
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-US')} at ${new Date().toLocaleTimeString('en-US')}`, margin, y);

      y = pageHeight - 10;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('LoyalFlow - Business Management System', pageWidth / 2, y, { align: 'center' });

      const fileName = `invoice_${invoice.stripe_invoice_id || invoice.id.substring(0, 8)}_${new Date().getTime()}.pdf`;
      doc.save(fileName);

      toast.success('החשבונית הורדה בהצלחה');
    } catch (error: any) {
      toast.error('שגיאה בהורדת החשבונית');
    } finally {
      setDownloadingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-sm text-gray-600">אין חשבוניות זמינות</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">היסטוריית חשבוניות</h3>
        <button
          onClick={loadInvoices}
          className="p-2 text-gray-500 active:text-gray-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {invoices.map((invoice, index) => (
          <motion.div
            key={invoice.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {invoice.stripe_invoice_id || `חשבונית ${invoice.id.substring(0, 8)}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span>{getPaymentTypeText(invoice.payment_type)}</span>
                    <span>•</span>
                    <span>
                      {invoice.paid_at
                        ? new Date(invoice.paid_at).toLocaleDateString('he-IL', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => downloadInvoice(invoice)}
                disabled={downloadingId === invoice.id}
                className="p-2 text-blue-600 active:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {downloadingId === invoice.id ? (
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${getStatusColor(invoice.status)}`}>
                  {getStatusIcon(invoice.status)}
                  <span>{getStatusText(invoice.status)}</span>
                </div>
              </div>

              <div className="text-left">
                <div className="text-sm font-bold text-gray-900">
                  {invoice.currency.toUpperCase()} {(invoice.amount - invoice.refund_amount).toFixed(2)}
                </div>
                {invoice.refund_amount > 0 && (
                  <div className="text-xs text-gray-500">
                    הוחזר: {invoice.currency.toUpperCase()} {invoice.refund_amount.toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {invoice.period_start && invoice.period_end && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>תקופת חיוב:</span>
                  <span>
                    {new Date(invoice.period_start).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })}
                    {' - '}
                    {new Date(invoice.period_end).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
